import { useEffect } from 'react';
import { engine } from '../core/audio/AudioEngine';
import { KEY_TO_DRUM, KEY_TO_NOTE } from '../core/midi/KeyMap';
import { useAppStore } from '../state/store';

/**
 * Software keyboard: the top computer-keyboard row plays the selected
 * channel (piano layout on synth channels, drum rows on drum channels).
 * Space toggles transport.
 */
export function useSoftwareKeyboard(): void {
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      // Undo/redo (Ctrl/Cmd+Z, Ctrl+Shift+Z, Ctrl+Y) — handled before the
      // modifier-key early return.
      if ((e.ctrlKey || e.metaKey) && !e.altKey) {
        const key = e.key.toLowerCase();
        const store = useAppStore.getState();
        if (key === 'z' && !e.shiftKey) {
          e.preventDefault();
          store.undo();
          return;
        }
        if ((key === 'z' && e.shiftKey) || key === 'y') {
          e.preventDefault();
          store.redo();
          return;
        }
      }
      if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      if (e.code === 'Space') {
        e.preventDefault();
        void useAppStore.getState().togglePlay();
        return;
      }
      const key = e.key.toLowerCase();
      const { project, selectedChannelId } = useAppStore.getState();
      const ch = project.channels.find((c) => c.id === selectedChannelId);
      if (!ch) return;
      if (ch.kind === 'drum') {
        const note = KEY_TO_DRUM[key];
        if (note !== undefined) engine.noteOn(ch.id, note);
      } else {
        // Synth and sampler channels use the piano layout.
        const note = KEY_TO_NOTE[key];
        if (note !== undefined) engine.noteOn(ch.id, note);
      }
    };

    const up = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      const { project, selectedChannelId } = useAppStore.getState();
      const ch = project.channels.find((c) => c.id === selectedChannelId);
      if (!ch) return;
      if (ch.kind === 'synth') {
        // Samplers are one-shot (gated) — no note-off needed.
        const note = KEY_TO_NOTE[key];
        if (note !== undefined) engine.noteOff(ch.id, note);
      }
    };

    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);
}